import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { fetchTravelData, approveTravelData } from "../../../api/travelActivityApi";
import { fetchUsers } from "../../../api/userApi";
import { fetchMdoList } from "../../../api/mdoApi";
import MantisDataTable from "../../../components/common/MantisDataTable";
import { toast } from "react-toastify";
import { useAuth } from "../../../sections/auth/AuthContext";

const TravelUpdate = () => {
  const [form, setForm] = useState({
    empCode: "",
    user_type: "Employee",
    travle_date: "",
  });

  const [travelData, setTravelData] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
   const { userInfo } = useAuth();
  // Travel mode options
  const travelModes = [
    "Public Transport",
    "No Travel",
    "Team Members Vehicle",
    "Own Four Wheeler",
    "Own Two Wheeler",
  ];

  // Load Employee/MDO list
  useEffect(() => {
    const loadEntities = async () => {
      try {
        let list = [];
        if (form.user_type === "Employee") list = await fetchUsers(userInfo);
        else list = await fetchMdoList(userInfo);
        setEntityList([
          { employee_name: "All", employee_code: "all" },
          ...(list || []),
        ]);
      } catch (err) {
        console.error("Failed to fetch entity list", err);
      }
    };
    loadEntities();
  }, [form.user_type]);

  const handleFilterChange = (filters) =>
    setForm((prev) => ({ ...prev, ...filters }));

  const handleFilterSubmit = async (filters) => {
    try {
      const payload = {
        emp_code: filters.empCode,
        travle_date: filters.travle_date,
      };
      const resp = await fetchTravelData(payload);
      if (!resp || resp.length === 0) {
        setTravelData([]);
        toast.info("No travel data available for the selected date");
      } else {
        setTravelData(resp);
      }
    } catch (err) {
      console.error("Error fetching travel data", err);
      setTravelData([]);
    }
  };

  const handleEdit = (row) => {
    setEditForm({
      empCode: row.p_emp_code_,
      travelMode: row.p_travel_mode,
      startTrId: row.p_start_tr_id,
      startTime: row.p_start_time,
      startOdoReading: row.p_start_odo_reading,
      endTrId: row.p_end_tr_id,
      endTime: row.p_end_time,
      endOdoReading: row.p_end_odo_reading,
    });
    setEditOpen(true);
  };

  const handleAdd = () => {
    if (!form.empCode || !form.travle_date) {
      toast.error("Please select Employee/MDO and Travel Date before adding");
      return;
    }

    setEditForm({
      empCode: form.empCode,
      travelMode: "",
      startTrId: null,
      startTime: form.travle_date + "T09:00", // use selected date
      startOdoReading: "",
      endTrId: null,
      endTime: form.travle_date + "T18:00", // use selected date
      endOdoReading: "",
    });
    setEditOpen(true);
  };

  const validateForm = () => {
    if (!editForm.travelMode) {
      toast.error("Travel Mode is required");
      return false;
    }
    if (!editForm.startTime) {
      toast.error("Start Time is required");
      return false;
    }
    if (!editForm.startOdoReading) {
      toast.error("Start Odo Reading is required");
      return false;
    }
    if (!editForm.endTime) {
      toast.error("End Time is required");
      return false;
    }
    if (!editForm.endOdoReading) {
      toast.error("End Odo Reading is required");
      return false;
    }
    return true;
  };

  const handleEditSave = async () => {
    if (!validateForm()) return;

    try {
      let payload;
      let isEdit = !!(editForm.startTrId || editForm.endTrId);

      if (isEdit) {
        payload = {
          empCode: editForm.empCode || form.empCode,
          travelMode: editForm.travelMode,
          startTrId: editForm.startTrId,
          startTime: editForm.startTime,
          startOdoReading: editForm.startOdoReading,
          endTrId: editForm.endTrId,
          endTime: editForm.endTime,
          endOdoReading: editForm.endOdoReading,
        };
      } else {
        payload = {
          empCode: form.empCode,
          travelMode: editForm.travelMode,
          startTrId: null,
          startTime: editForm.startTime,
          startOdoReading: editForm.startOdoReading,
          endTrId: null,
          endTime: editForm.endTime,
          endOdoReading: editForm.endOdoReading,
        };
      }

      await approveTravelData(payload);

      toast.success(isEdit ? "Travel record updated successfully" : "Travel record added successfully");

      setEditOpen(false);
      handleFilterSubmit(form); // refresh table
    } catch (err) {
      console.error("Failed to save travel data", err);
      toast.error("Failed to save travel data");
    }
  };

  const TravelColumns = [
    {
      label: "Actions",
      type: "actions",
      actions: [
        {
          type: "edit",
          action: handleEdit,
        },
      ],
    },
    { key: "p_start_time", label: "Start Time" },
    { key: "p_start_odo_reading", label: "Start Odo Reading" },
    { key: "p_end_time", label: "End Time" },
    { key: "p_end_odo_reading", label: "End Odo Reading" },
    { key: "p_total_km", label: "Total Km" },
    { key: "p_travel_mode", label: "Travel Mode" },
  ];

  return (
    <>
      <MantisDataTable
        title="Travel Update"
        data={travelData}
        columns={TravelColumns}
        searchable={false}
        showAddButton={true}
        onAdd={handleAdd}
        filters={[
          {
            label: "User Type",
            name: "user_type",
            type: "select",
            options: [
              { label: "Payroll Employee", value: "Employee" },
              { label: "Third-party Associates", value: "MDO" },
            ],
          },
          {
            label: form.user_type === "Employee" ? "Employee" : "MDO",
            name: "empCode",
            type: "select",
            options: entityList.map((ent) => ({
              label: ent.employee_name,
              value: ent.employee_code,
            })),
          },
          {
            label: "Travel Date",
            name: "travle_date",
            type: "date",
            InputLabelProps: { shrink: true },
          },
        ]}
        onFilterChange={handleFilterChange}
        onFilterSubmit={handleFilterSubmit}
      />

      {/* Edit Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editForm?.startTrId || editForm?.endTrId ? "Edit Travel Record" : "Add Travel Record"}
        </DialogTitle>

        <DialogContent dividers>
          {editForm && (
            <>
              <TextField
                margin="dense"
                label="Travel Mode *"
                name="travelMode"
                select
                fullWidth
                required
                value={editForm.travelMode}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, travelMode: e.target.value }))
                }
              >
                {travelModes.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {mode}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                margin="dense"
                label="Start Time *"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                value={editForm.startTime}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, startTime: e.target.value }))
                }
              />

              <TextField
                margin="dense"
                label="Start Odo Reading *"
                fullWidth
                required
                value={editForm.startOdoReading}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, startOdoReading: e.target.value }))
                }
              />

              <TextField
                margin="dense"
                label="End Time *"
                type="datetime-local"
                fullWidth
                required
                value={editForm.endTime}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, endTime: e.target.value }))
                }
              />

              <TextField
                margin="dense"
                label="End Odo Reading *"
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                value={editForm.endOdoReading}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, endOdoReading: e.target.value }))
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TravelUpdate;
